import { map } from "@prismicio/editor-support/Set"
import { useRequest } from "@prismicio/editor-support/Suspense"
import type { FilterOption } from "@prismicio/editor-ui"
import { useSuspenseQuery } from "@tanstack/react-query"

import { searchDocumentStatus } from "../../../core/document"
import {
  type DocumentMeta,
  type DocumentSearchMeta,
  getRepository,
  searchDocumentsQueryOptions,
} from "../../../core/service"
import { throwConfigError, useEditorConfig } from "../../../EditorConfig"

export function useDocuments(params: {
  searchTerm: string
  tagsFilter: Set<FilterOption>
  typesFilter: Set<FilterOption>
  localesFilter: Set<FilterOption>
}) {
  const { searchTerm, tagsFilter, typesFilter, localesFilter } = params
  const config = useEditorConfig()

  if (!config.searchDocuments) throwConfigError("searchDocuments")

  const { storageVersion } = useRequest(getRepository, [
    config.coreApiBaseUrl,
    config.authStrategy,
  ])

  if (!config.searchDocuments) throwConfigError("searchDocuments")
  const { status } = config

  const { data } = useSuspenseQuery({
    ...searchDocumentsQueryOptions({
      searchTerm,
      tags: [...map(tagsFilter, (tag) => tag.id)],
      customTypes: [...map(typesFilter, (type) => type.id)],
      locale: [...map(localesFilter, (locale) => locale.id)][0] ?? "",
      status: searchDocumentStatus[status],
      storageVersion,
      config,
    }),
    select: (data) =>
      filterDocumentsByStatus(data.results, searchDocumentStatus[status]),
    // FIXME: staleTime is set to 300 to prevent the cache from being invalidated immediately,
    // as the modal rerenders too often, thus running multiple requests on search params change
    // https://linear.app/prismic/issue/PBD-1871/editor-fields-documents-modal-has-unoptimised-renders
    staleTime: 300,
  })

  return {
    documents: data.results,
  }
}

//FIXME: status filter isn't working properly, filter documents by multiple searchDocumentStatus locally
// https://linear.app/prismic/issue/PBD-1846/[frontend]-add-status-filter-in-the-search-modal
function filterDocumentsByStatus(
  documents: (DocumentMeta | DocumentSearchMeta)[],
  status: string[],
) {
  const documentsFilteredByStatus = documents.flatMap((document) => {
    const versions = document.versions

    const filteredVersions = versions.filter((version) =>
      status.includes(version.status),
    )

    if (filteredVersions.length === 0) return []

    return {
      ...document,
      versions: filteredVersions,
    }
  })

  return { results: documentsFilteredByStatus }
}
